"use client";

import { useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { X } from "lucide-react";
import { Button } from "@eg/ui";
import { useCartStore } from "@/store/cart";
import { fetchJSON } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import {
  trackBeginCheckout,
  trackSelectPickup,
  trackSelectShip,
} from "@/lib/analytics";
import { useCartUI } from "./CartUIProvider";
import { useLocale, useTranslations } from "@/lib/i18n/I18nProvider";

function shippingCentsByMethod(method?: string) {
  if (!method || method.toLowerCase() === "standard") return 490;
  if (method.toLowerCase() === "express") return 990;
  return 490;
}

function getFocusableElements(root: HTMLElement) {
  const selectors = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ];
  return Array.from(
    root.querySelectorAll<HTMLElement>(selectors.join(",")),
  ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
}

export function CartDrawer() {
  const { isCartOpen, closeCart } = useCartUI();
  const router = useRouter();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const t = useTranslations();
  const locale = useLocale();

  const {
    items,
    updateQuantity,
    removeItem,
    clearCart,
    totalCents,
    fulfillment,
    setFulfillment,
  } = useCartStore();

  const currency = items[0]?.currency || "EUR";
  const itemCount = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );
  const shippingCents = useMemo(() => {
    return fulfillment.mode === "ship"
      ? shippingCentsByMethod(fulfillment.detail || "Standard")
      : 0;
  }, [fulfillment.detail, fulfillment.mode]);
  const subtotalCents = totalCents();
  const grandTotalCents = subtotalCents + shippingCents;

  useEffect(() => {
    if (!isCartOpen) return;
    requestAnimationFrame(() => closeButtonRef.current?.focus());
  }, [isCartOpen]);

  useEffect(() => {
    const root = drawerRef.current;
    if (!root) return;
    if (isCartOpen) {
      root.removeAttribute("inert");
    } else {
      root.setAttribute("inert", "");
    }
  }, [isCartOpen]);

  useEffect(() => {
    if (!isCartOpen) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeCart();
        return;
      }
      if (e.key !== "Tab") return;
      const root = drawerRef.current;
      if (!root) return;

      const focusables = getFocusableElements(root);
      if (focusables.length === 0) return;

      const active = document.activeElement as HTMLElement | null;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (!active || active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!active || !root.contains(active)) {
          e.preventDefault();
          first.focus();
        } else if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeCart, isCartOpen]);

  async function checkout() {
    if (items.length === 0) return;
    try {
      trackBeginCheckout({
        totalCents: grandTotalCents,
        currency,
        items: items.map((i) => ({
          variantId: i.variantId,
          quantity: i.quantity,
        })),
      });
      const data = await fetchJSON<{ id: string; url?: string }>(
        "/checkout/session",
        {
          method: "POST",
          body: JSON.stringify({
            items: items.map((i) => ({
              variantId: i.variantId,
              quantity: i.quantity,
            })),
            // Stripe ersetzt das Platzhalter-Token beim Redirect
            successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/checkout/cancel`,
            fulfillmentMode: fulfillment.mode === "pickup" ? "PICKUP" : "SHIP",
            pickupLocation:
              fulfillment.mode === "pickup"
                ? fulfillment.detail || "Store Gummersbach"
                : undefined,
            shippingMethod:
              fulfillment.mode === "ship"
                ? fulfillment.detail || "Standard"
                : undefined,
          }),
        },
      );
      if (data.url) window.location.href = data.url as string;
    } catch (e) {
      console.error(e);
      alert(t("cart.checkoutFailed"));
    }
  }

  return (
    <div
      ref={drawerRef}
      className={[
        "fixed right-0 top-0 z-[70] h-dvh w-full transform transition-transform duration-300 ease-out sm:w-[430px]",
        isCartOpen ? "translate-x-0" : "pointer-events-none translate-x-full",
      ].join(" ")}
      role="dialog"
      aria-modal={isCartOpen ? "true" : "false"}
      aria-label={t("cart.aria.cart")}
      aria-hidden={isCartOpen ? "false" : "true"}
    >
      <div className="flex h-full flex-col border-l border-slate-200/80 bg-[radial-gradient(circle_at_8%_0%,rgba(186,230,253,0.24),transparent_40%),radial-gradient(circle_at_95%_2%,rgba(187,247,208,0.2),transparent_38%),linear-gradient(175deg,rgba(255,255,255,0.98),rgba(241,249,236,0.96))] shadow-[0_22px_52px_rgba(15,23,42,0.24)]">
        <div className="border-b border-slate-200/80 bg-white/65 px-4 py-3 backdrop-blur-md sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.17em] text-slate-500">
                Checkout
              </p>
              <h2 className="mt-1 font-heading text-2xl font-semibold text-slate-900">
                {t("cart.title")}
              </h2>
              <p className="text-xs font-semibold text-slate-600">
                {t("cart.items", { count: itemCount })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {items.length > 0 ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="home-focus rounded-xl border border-slate-300/80 bg-white/80 text-slate-700 hover:bg-slate-50"
                  onClick={clearCart}
                >
                  {t("common.clear")}
                </Button>
              ) : null}
              <Button
                ref={closeButtonRef}
                variant="ghost"
                size="icon"
                className="home-focus rounded-full border border-slate-300/80 bg-white/85 text-slate-700 hover:bg-white"
                onClick={closeCart}
                aria-label={t("cart.aria.close")}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {items.length === 0 ? (
            <div className="home-panel space-y-4 rounded-2xl p-5">
              <div className="text-base font-semibold text-slate-900">
                {t("cart.emptyTitle")}
              </div>
              <div className="text-sm leading-relaxed text-slate-600">
                {t("cart.emptyCopy")}
              </div>
              <Button
                className="home-focus rounded-xl border border-cyan-500/35 bg-cyan-100/80 text-cyan-900 hover:bg-cyan-100"
                onClick={() => {
                  closeCart();
                  router.push("/produkte");
                }}
              >
                {t("cart.emptyCta")}
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-3.5">
                {items.map((item) => (
                  <article
                    key={item.variantId}
                    className="home-panel rounded-2xl p-3.5 sm:p-4"
                  >
                    <div className="flex items-start gap-3">
                      {item.image ? (
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-slate-200/75 bg-white/80 sm:h-[72px] sm:w-[72px]">
                          <img
                            src={item.image}
                            alt={item.productTitle}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white/70 text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:h-[72px] sm:w-[72px]">
                          {t("cart.noImage")}
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="inline-flex max-w-full rounded-full border border-slate-300/75 bg-white/75 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                          <span className="truncate">{item.variantTitle}</span>
                        </p>
                        <h3 className="mt-1.5 text-sm font-bold leading-snug text-slate-900">
                          {item.productTitle}
                        </h3>
                        <p className="mt-1.5 text-sm font-semibold text-cyan-800">
                          {formatPrice(item.priceCents, item.currency, locale)}
                        </p>
                      </div>

                      <Button
                        size="sm"
                        variant="outline"
                        className="home-focus rounded-xl border-slate-300/85 bg-white/85 text-slate-700 hover:border-cyan-400/55 hover:bg-cyan-50"
                        onClick={() => removeItem(item.variantId)}
                      >
                        {t("common.remove")}
                      </Button>
                    </div>

                    <div className="mt-3.5 flex items-center justify-between gap-3 border-t border-slate-200/80 pt-3">
                      <div className="flex items-center gap-2">
                        <button
                          className="home-focus flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300/85 bg-white text-lg font-semibold text-slate-700 transition hover:border-cyan-400/55 hover:bg-cyan-50"
                          onClick={() =>
                            updateQuantity(item.variantId, item.quantity - 1)
                          }
                          aria-label={t("cart.aria.qtyDec")}
                        >
                          -
                        </button>
                        <div className="w-10 text-center text-base font-bold text-slate-900">
                          {item.quantity}
                        </div>
                        <button
                          className="home-focus flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300/85 bg-white text-lg font-semibold text-slate-700 transition hover:border-cyan-400/55 hover:bg-cyan-50"
                          onClick={() =>
                            updateQuantity(item.variantId, item.quantity + 1)
                          }
                          aria-label={t("cart.aria.qtyInc")}
                        >
                          +
                        </button>
                      </div>

                      <div className="text-lg font-bold text-slate-900">
                        {formatPrice(
                          item.priceCents * item.quantity,
                          item.currency,
                          locale,
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <section className="home-panel rounded-2xl p-4">
                <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-slate-700">
                  {t("cart.fulfillment.title")}
                </h3>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    className={`home-focus rounded-xl border px-3 py-2.5 text-left transition ${
                      fulfillment.mode === "ship"
                        ? "border-cyan-500/55 bg-cyan-100/80 text-cyan-900"
                        : "border-slate-300/85 bg-white/82 text-slate-700 hover:border-cyan-300/60 hover:bg-cyan-50/60"
                    }`}
                    onClick={() => {
                      setFulfillment("ship", fulfillment.detail || "Standard");
                      trackSelectShip(fulfillment.detail || "Standard");
                    }}
                  >
                    <div className="text-sm font-bold">
                      {t("cart.fulfillment.shipTitle")}
                    </div>
                    <div className="text-xs font-medium text-slate-600">
                      {t("cart.fulfillment.shipCopy")}
                    </div>
                  </button>
                  <button
                    className={`home-focus rounded-xl border px-3 py-2.5 text-left transition ${
                      fulfillment.mode === "pickup"
                        ? "border-emerald-500/55 bg-emerald-100/80 text-emerald-900"
                        : "border-slate-300/85 bg-white/82 text-slate-700 hover:border-emerald-300/60 hover:bg-emerald-50/60"
                    }`}
                    onClick={() => {
                      setFulfillment("pickup", "Store Gummersbach");
                      trackSelectPickup("Store Gummersbach");
                    }}
                  >
                    <div className="text-sm font-bold">
                      {t("cart.fulfillment.pickupTitle")}
                    </div>
                    <div className="text-xs font-medium text-slate-600">
                      {t("cart.fulfillment.pickupCopy")}
                    </div>
                  </button>
                </div>

                {fulfillment.mode === "ship" ? (
                  <div className="mt-4 space-y-2.5">
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-600">
                      {t("cart.fulfillment.shippingMethodTitle")}
                    </div>
                    {["Standard", "Express"].map((method) => (
                      <button
                        key={method}
                        className={`home-focus flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left transition ${
                          (fulfillment.detail || "Standard").toLowerCase() ===
                          method.toLowerCase()
                            ? "border-cyan-500/55 bg-cyan-100/78 text-cyan-900"
                            : "border-slate-300/85 bg-white/82 hover:border-cyan-300/60 hover:bg-cyan-50/60"
                        }`}
                        onClick={() => {
                          setFulfillment("ship", method);
                          trackSelectShip(method);
                        }}
                      >
                        <div>
                          <div className="text-sm font-bold">{method}</div>
                          <div className="text-xs font-medium text-slate-600">
                            {method === "Standard"
                              ? t("cart.fulfillment.shippingStandardEta")
                              : t("cart.fulfillment.shippingExpressEta")}
                          </div>
                        </div>
                        <div className="text-sm font-bold text-slate-900">
                          {formatPrice(
                            shippingCentsByMethod(method),
                            currency,
                            locale,
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 rounded-xl border border-emerald-300/60 bg-emerald-100/45 px-3 py-3 text-sm font-medium leading-relaxed text-emerald-900">
                    {t("cart.fulfillment.pickupHint")}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-slate-200/80 bg-white/70 px-4 py-4 backdrop-blur-md sm:px-5">
          <div className="rounded-2xl border border-slate-200/85 bg-white/86 px-4 py-3 shadow-[0_12px_26px_rgba(30,41,59,0.08)]">
            <div className="flex items-center justify-between text-sm font-medium text-slate-700">
              <span>{t("cart.summary.subtotal")}</span>
              <span>{formatPrice(subtotalCents, currency, locale)}</span>
            </div>
            <div className="mt-1.5 flex items-center justify-between text-sm font-medium text-slate-700">
              <span>{t("cart.summary.shipping")}</span>
              <span>{formatPrice(shippingCents, currency, locale)}</span>
            </div>
            <div className="mt-2.5 flex items-center justify-between border-t border-slate-200 pt-2.5 text-2xl font-bold text-slate-900">
              <span>{t("cart.summary.total")}</span>
              <span>{formatPrice(grandTotalCents, currency, locale)}</span>
            </div>
          </div>

          <Button
            className="home-focus w-full rounded-xl border border-cyan-500/30 bg-gradient-to-r from-emerald-300 to-cyan-300 text-[15px] font-bold text-slate-950 shadow-[0_14px_26px_rgba(34,197,94,0.2)] transition hover:-translate-y-0.5 hover:from-emerald-200 hover:to-cyan-200"
            disabled={items.length === 0}
            onClick={checkout}
          >
            {t("cart.pay")}
          </Button>

          <Button
            className="home-focus w-full rounded-xl border border-slate-300/85 bg-white/82 text-slate-800 transition hover:border-cyan-400/50 hover:bg-cyan-50"
            variant="secondary"
            onClick={closeCart}
          >
            {t("cart.continueShopping")}
          </Button>

          <p className="px-0.5 text-xs font-medium leading-relaxed text-slate-500">
            {t("cart.stripeHint")}
          </p>
        </div>
      </div>
    </div>
  );
}
